import type { HighlighterCore, ThemedToken, ThemeRegistration } from 'shiki/core';

/**
 * Code highlighting with Shiki, loaded only when a message has a code block, and each language
 * only when it first appears. Colours follow the approved design's code block.
 */
const theme: ThemeRegistration = {
  name: 'novachat',
  type: 'dark',
  colors: { 'editor.background': '#0F1217', 'editor.foreground': '#D4D9E0' },
  settings: [
    { settings: { foreground: '#D4D9E0' } },
    { scope: ['comment', 'punctuation.definition.comment'], settings: { foreground: '#7D8694', fontStyle: 'italic' } },
    { scope: ['keyword', 'storage', 'storage.type', 'storage.modifier', 'keyword.control', 'keyword.operator.new', 'entity.name.tag'], settings: { foreground: '#ED8B5D' } },
    { scope: ['entity.name.function', 'support.function', 'variable.function', 'meta.function-call', 'entity.other.attribute-name'], settings: { foreground: '#6ACBDC' } },
    { scope: ['string', 'string.quoted', 'string.template', 'punctuation.definition.string', 'markup.inserted'], settings: { foreground: '#98DBAA' } },
    { scope: ['constant.numeric', 'constant.language', 'constant.character', 'support.constant', 'variable.other.constant'], settings: { foreground: '#F3A37B' } },
    { scope: ['entity.name.type', 'entity.name.class', 'support.type', 'support.class', 'entity.other.inherited-class'], settings: { foreground: '#A2A3D8' } },
    { scope: ['punctuation', 'meta.brace', 'keyword.operator'], settings: { foreground: '#A9B1BD' } },
    { scope: ['markup.deleted'], settings: { foreground: '#EE9191' } },
    { scope: ['markup.heading', 'markup.bold'], settings: { foreground: '#ECEFF3', fontStyle: 'bold' } },
  ],
};

// Explicit imports so the bundler can split each grammar into its own file.
const grammars = {
  typescript: () => import('shiki/langs/typescript.mjs'),
  tsx: () => import('shiki/langs/tsx.mjs'),
  javascript: () => import('shiki/langs/javascript.mjs'),
  jsx: () => import('shiki/langs/jsx.mjs'),
  json: () => import('shiki/langs/json.mjs'),
  php: () => import('shiki/langs/php.mjs'),
  blade: () => import('shiki/langs/blade.mjs'),
  sql: () => import('shiki/langs/sql.mjs'),
  bash: () => import('shiki/langs/bash.mjs'),
  yaml: () => import('shiki/langs/yaml.mjs'),
  python: () => import('shiki/langs/python.mjs'),
  swift: () => import('shiki/langs/swift.mjs'),
  kotlin: () => import('shiki/langs/kotlin.mjs'),
  go: () => import('shiki/langs/go.mjs'),
  html: () => import('shiki/langs/html.mjs'),
  css: () => import('shiki/langs/css.mjs'),
  dockerfile: () => import('shiki/langs/dockerfile.mjs'),
  nginx: () => import('shiki/langs/nginx.mjs'),
  diff: () => import('shiki/langs/diff.mjs'),
  markdown: () => import('shiki/langs/markdown.mjs'),
} as const;
type Grammar = keyof typeof grammars;

const aliases: Record<string, Grammar> = {
  ts: 'typescript',
  js: 'javascript',
  mjs: 'javascript',
  cjs: 'javascript',
  sh: 'bash',
  shell: 'bash',
  zsh: 'bash',
  console: 'bash',
  yml: 'yaml',
  py: 'python',
  kt: 'kotlin',
  golang: 'go',
  docker: 'dockerfile',
  md: 'markdown',
  postgres: 'sql',
  postgresql: 'sql',
  mysql: 'sql',
  vue: 'html',
};

/** The grammar for a fence's language tag (```ts), or null when we have none. */
export function grammarFor(tag: string | undefined): Grammar | null {
  if (!tag) return null;
  const key = tag.toLowerCase();
  if (key in grammars) return key as Grammar;
  return aliases[key] ?? null;
}

let highlighter: Promise<HighlighterCore> | null = null;
const loaded = new Map<Grammar, Promise<void>>();

function core(): Promise<HighlighterCore> {
  highlighter ??= Promise.all([import('shiki/core'), import('shiki/engine/javascript')]).then(([{ createHighlighterCore }, { createJavaScriptRegexEngine }]) =>
    createHighlighterCore({ themes: [theme], langs: [], engine: createJavaScriptRegexEngine() }),
  );
  return highlighter;
}

/** Coloured lines for `code`; rejects if the grammar cannot load (the caller keeps plain text). */
export async function tokenize(code: string, grammar: Grammar): Promise<ThemedToken[][]> {
  const shiki = await core();
  let loading = loaded.get(grammar);
  if (!loading) {
    loading = grammars[grammar]().then((module) => shiki.loadLanguage((module as { default: Parameters<HighlighterCore['loadLanguage']>[0] }).default));
    loaded.set(grammar, loading);
  }
  await loading;
  return shiki.codeToTokens(code, { lang: grammar, theme: 'novachat' }).tokens;
}
